<template>
    <md-card class="ql-page" id="ql-page" :key="handler" :style="pageColumnWidth()">
        <md-dialog :md-open-from="`cardCover${pageNumber}`" :md-close-to="`cardCover${pageNumber}`"
            ref="no-layers-dialog">
            <md-dialog-title>You don't have an Active Layer selected!</md-dialog-title>
            <md-dialog-content>Open up the layers menu and choose a layer to edit.</md-dialog-content>
            <md-dialog-actions>
                <md-button class="md-primary" @click="closeDialog('no-layers-dialog')">Ok</md-button>
            </md-dialog-actions>
        </md-dialog>
        <md-dialog :md-open-from="`cardCover${pageNumber}`" :md-close-to="`cardCover${pageNumber}`"
            ref="no-collaborator-dialog">
            <md-dialog-title>Observer accounts are unable to add notes and cues.</md-dialog-title>
            <md-dialog-actions>
                <md-button class="md-primary" @click="closeDialog('no-collaborator-dialog')">Ok</md-button>
            </md-dialog-actions>
        </md-dialog>
        <md-dialog 
            :md-open-from="`cardCover${pageNumber}`" 
            :md-close-to="`cardCover${pageNumber}`" 
            ref="banner-dialog"
            :md-click-outside-to-close="false"
            :md-esc-to-close="false"
            class="alert-banner-dialog"
        >
            <md-dialog-content class="alert-content" v-if="editedUser">
                <div class="alert-box">
                    <div class="alert-icon-box">
                        <md-icon class="alert-icon">warning</md-icon>
                    </div>
                    <div class="alert-text-box">
                        <h3 class="alert-title">Hold, please!</h3>
                        <p class="alert-message">
                            <span style="font-weight: bold;">{{ this.editedUser || 'A collaborator' }}</span> is currently adding, deleting, or replacing pdf pages in this project. Please wait until the action is completed or canceled.
                            <span v-if="project.ownerUID === user.uid" style="display: block; margin-top: 8px;">As the owner, you can take over by clicking the close button below.</span>
                        </p>
                    </div>
                </div>
            </md-dialog-content>
            <md-dialog-actions v-if="project.ownerUID === user.uid">
                <md-button class="md-primary" @click="closeAlert">Close anyway</md-button>
            </md-dialog-actions>
        </md-dialog>
        <md-card-media-cover :id="`cardCover${pageNumber}`" ref="cardCover" class="ql-page-cover">
            <md-button v-if="pageReady" class="btnBookmark md-icon-button" @click="togglePageBookmark" :style="bookmarkStyle">
                <md-icon>{{ bookmarked ? 'bookmark' : 'bookmark_border' }}</md-icon>
                <md-tooltip>{{ bookmarked ? 'Remove from My Bookmarks' : 'Add to My Bookmarks' }}</md-tooltip>
            </md-button>
            <project-pdf-page class="ql-pdf-page" :class="{'night-mode': showMode, 'disable-text': disableText}"
                v-if="ready"
                :page="page"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                :currentCueLayer="currentCueLayer"
                :locked="locked"
                :issrc="issrc"
                :openCues="openCues"
                @pdfLoaded="pdfLoaded"
                :positionedCues="positionedCues"
                @updatePageHistory="updatePageHistory"
                :svgTrueHeight="svgTrueHeight"
                @cueCreated="cueCreated"
                @addNote="addNote"
                @openDialog="openDialog"
                :selectedElement="selectedElement"
            />
            <p v-if="!ready">Loading PDF...</p>
            <resize-sensor @resize="resize"></resize-sensor>
        </md-card-media-cover>
        <div
            class="marker-container"
            v-if="pageReady"
            :style="markerContainerStyle()"
            :class="{ 'marker-container--place-mode': canPlaceScriptMarker, 'marker-container--reach-marks': !cueTakesPress }"
            @dblclick="onMarkerContainerPlace"
            @touchstart="onMarkerContainerTouchStart"
            @touchend="onMarkerContainerTouchEnd"
            @touchcancel="onMarkerContainerTouchEnd"
            @touchmove="onMarkerContainerTouchMove"
        >
            <cue-position-marker v-for="cue in positionCues"
                :cue="cue"
                :key="`${cue.id}${versionID}`"
                :editable="isCueEditable(cue)"
                :locked="locked"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                @updateCue="updateCue"
                @activated="onCueActivated(cue)"
                @deactivated="onCueDeactivated(cue)">
            </cue-position-marker>
            <cue-selection-marker v-for="cue in selectionCues"
                :cue="cue"
                :key="`${cue.id}${versionID}`"
                :editable="isCueEditable(cue)"
                :locked="locked"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                @updateCue="updateCue"
                @activated="onCueActivated(cue)"
                @deactivated="onCueDeactivated(cue)">
            </cue-selection-marker>
            <script-marker-overlay
                v-for="marker in scriptMarkersOnPage"
                :key="`${marker.markerId || marker.id}-${pageWidth}-${pageHeight}`"
                :marker="marker"
                :editable="canEditStructure && !locked"
                :locked="locked"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                :startInlineEdit="(marker.markerId || marker.id) === pendingInlineEditMarkerId"
                @updateMarker="updateScriptMarker"
                @deleteMarker="deleteScriptMarker"
                @inlineEditDone="clearPendingInlineEdit"
                @activated="disableText = true"
                @deactivated="disableText = false"
            />
            <!-- Draw strokes paint ABOVE structure marks (Josh, 10 Oct 2026); highlights and other annotations stay under.
                 Annotations live in .ql-note-container, under this whole container, so each visible drawing on a page
                 with structure marks gets a draw-only copy here: same SVG, same box, pointer-events none. The real
                 drawing keeps its place for clicks and is hidden while its copy shows; a selected drawing shows its
                 real self so dragging is live. -->
            <div v-for="note in drawingsPaintedAbove"
                class="ql-note-free-drawing ql-drawing-paint-only"
                :key="`paint-${note.id}${versionID}`"
                :style="drawingPaintStyle(note)">
                <div class="note-free_drawing" v-html="note.text"></div>
            </div>
        </div>

        <div class="ql-note-container" v-if="pageReady" :style="{left: `${offsetX}px`, width: `${pdfWidth}px`}">
            <project-note-template
                v-for="note in displayNotes"
                :key="`${note.id}${versionID}`"
                :note="note"
                :locked="!isNoteEditable(note) || locked"
                :class="{ 'ql-drawing-hit-only': isDrawingPaintedAbove(note) }"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                :totalPages="totalPages"
                @setSelectedElement="setSelectedElement"
                :selectedElement="selectedElement"
                :currentNoteLayerBackground="currentNoteLayerBackground"
                @updatePageHistory="updatePageHistory"
                @copyElement="copyElement"
                @updateNote="updateNote"
                @activated="onNoteActivated(note)"
                @deactivated="onNoteDeactivated(note)">
            </project-note-template>
        </div>

        <!-- Transparent overlay for the in-progress free-drawing stroke. Kept a
             sibling of the note container (z-index 1001) so the live stroke is
             visible above image/note annotations exactly where the finalized
             drawing will land, instead of being painted onto the PDF canvas
             underneath them. pointer-events:none so it never blocks drawing. -->
        <canvas v-if="pageReady"
            :id="`ql-drawing-overlay-${pageNumber}`"
            class="ql-drawing-overlay"
            :style="{left: `${offsetX}px`, width: `${pdfWidth}px`, height: `${pdfHeight}px`}">
        </canvas>
    </md-card>
</template>

<script>
import { cueGeometryOk } from '@/lib/cue-geometry';
import resizeSensor from 'vue-resize-sensor';
import { mapGetters, mapActions } from 'vuex';
import { shouldWarnNoSeed } from '@/lib/autoNumberWarning';
import NavBus from '@/buses/nav';
import { cueForPaste, markForPaste } from '@/lib/cue-paste';
import { defaultMarkerLabel, markerTypeFromToolIndex } from '@/lib/marker-labels';
import CuePositionMarker from '@/components/project/cues/cue-position-marker';
import CueSelectionMarker from '@/components/project/cues/cue-selection-marker';
import ProjectPdfPage from '@/components/project/pdf-page';
import ProjectNoteTemplate from '@/components/project/notes/wrapper';
import { paintsAboveStructureMarks, cueTakesPress, drawingBox } from '@/lib/structure-mark-layering';
import ScriptMarkerOverlay from '@/components/project/markers/script-marker';

const comparator = (a, b) => {
    if (a.y === b.y) {
        if (a.x === b.x || a.x > b.x) {
            return 1;
        }
        return 0;
    } else if (a.y > b.y) {
        return 1;
    }
    return 0;
};

const ProjectPage = {
    name: 'projectPage',
    props: ['page', 'cues', 'locked', 'bookmarked', 'bookmarkNavOpened', 'issrc', 'positionedCues', 'svgTrueHeight', 'selectedElement', 'openCues'],
    data: function() {
        return {
            pageNumber: this.$props.page.pageNumber,
            pageContainerId: 'pageContainer' + this.$props.page.pageNumber,
            ready: false,
            pageReady: false,
            disableText: false,
            addingCue: false,
            addCueX: 0,
            addCueY: 0,
            cueToAdd: null,
            onCueAdded: null,
            offsetX: 0,
            offsetY: 0,
            pageWidth: 0,
            pageHeight: 0,
            pdfHeight: 0,
            pdfWidth: 0,
            originWidth: 0,
            originHeight: 0,
            cueMarkerWidth: 6,
            cueMarkerHeight: 6,
            handler: 0,
            projectBannerUserID: '',
            editedUser: null,
            pendingInlineEditMarkerId: null,
            markerLongPressTimer: null,
            activeNoteId: null,
            altHeld: false
        };
    },
    watch: {
        bookmarkNavOpened() {
            this.handler++;
        },
        projectBannerUser: {
            async handler(newVal) {
                if (newVal) {
                    this.projectBannerUserID = newVal;
                    const user = await this.userByUID(newVal);
                    this.editedUser = user ? (user.username || '') : '';
                } else {
                    this.projectBannerUserID = '';
                    this.editedUser = '';
                }
            },
            immediate: true
        },
        projectBannerDisplay: {
            handler(newVal) {
                if (newVal && (this.projectBannerUserID !== this.user.uid)) {
                    this.openDialog('banner-dialog');
                } else {
                    this.closeDialog('banner-dialog');
                }
            },
            immediate: true
        }
    },
    computed: {
        ...mapGetters([
            'user',
            'project',
            'currentVersion',
            'layerByCue',
            'visibleCues',
            'editableLayers',
            'currentCueLayer',
            'currentNoteLayer',
            'notesByPage',
            'showNotesLayerIDs',
            'selectedTool',
            'selectedToolIndex',
            'showMode',
            'selectedScale',
            'project',
            'projectTotalPages',
            'projectBannerDisplay',
            'projectID',
            'projectBannerUser',
            'userByUID',
            'visibleScriptMarkersByPage',
            'canEditStructure',
            'showCuesLayerIDs'
        ]),
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        displayCues: function() {
            return this.visibleCues(this.pageNumber);
        },
        selectionCues: function() {
            return this.displayCues.filter(c => c.type === 'selection');
        },
        positionCues: function() {
            return this.displayCues.filter(c => c.type === 'position');
        },
        displayNotes: function() {
            return this.notesByPage(this.pageNumber);
        },
        scriptMarkersOnPage: function() {
            return this.visibleScriptMarkersByPage(this.pageNumber);
        },
        drawingsPaintedAbove: function() {
            return this.displayNotes.filter(this.isDrawingPaintedAbove);
        },
        cueTakesPress: function() {
            return cueTakesPress({ altKey: this.altHeld });
        },
        bookmarkStyle: function() {
            return {
                position: 'absolute',
                top: '0px',
                left: (this.pageWidth - 46) + 'px',
                zIndex: '9999',
                color: '#5ecfdf',
                transition: 'none'
            };
        },
        currentNoteLayerBackground() {
            return (this.currentNoteLayer && this.currentNoteLayer.defaultTextStampBackground) || '';
        },
        totalPages: function() {
            if (this.versionID) {
                return this.projectTotalPages || 0;
            }
            
            return (this.project && Array.isArray(this.project.pdfPageNumbers)) 
                ? this.project.pdfPageNumbers.length 
                : 0;
        },
        projectBanner() {
            return this.projectBannerDisplay;
        },
        canPlaceScriptMarker() {
            return this.selectedTool === 'scriptMarker'
                && this.canEditStructure
                && !this.locked;
        },
        activeScriptMarkerType() {
            return markerTypeFromToolIndex(this.selectedToolIndex);
        }
    },
    methods: {
        onCueActivated() {
            this.disableText = true;
        },
        onCueDeactivated() {
            this.disableText = false;
        },
        // True when this drawing is shown by its draw-only copy above the structure marks (see the template). False
        // while it is selected, so the real drawing shows during drag.
        isDrawingPaintedAbove(note) {
            return this.scriptMarkersOnPage.length > 0
                && paintsAboveStructureMarks(note)
                && note.id !== this.activeNoteId;
        },
        drawingPaintStyle(note) {
            const box = drawingBox(note, {
                pageWidth: this.pageWidth,
                pageHeight: this.pageHeight,
                selectedScale: this.selectedScale
            });
            return {
                transform: `translate(${box.left}px, ${box.top}px)`,
                width: `${box.width}px`,
                height: `${box.height}px`
            };
        },
        onNoteActivated(note) {
            this.disableText = true;
            this.activeNoteId = note.id;
        },
        onNoteDeactivated(note) {
            this.disableText = false;
            if (this.activeNoteId === note.id) this.activeNoteId = null;
        },
        // Option/Alt held: cue markers let the press through to a structure mark underneath (see CSS).
        onAltKey(e) {
            this.altHeld = !!e.altKey;
        },
        clearAltKey() {
            this.altHeld = false;
        },
        ...mapActions(['addScriptMarker']),
        markerContainerStyle() {
            return {
                left: `${this.offsetX}px`,
                width: `${this.pdfWidth}px`,
                height: `${this.pageHeight}px`
            };
        },
        updateScriptMarker(markerToUpdate) {
            const marker = { ...markerToUpdate };
            if (marker.x > 1 || marker.y > 1) {
                marker.x /= this.pageWidth;
                if (marker.x > 1) marker.x = 1;
                marker.y /= this.pageHeight;
                if (marker.y > 1) marker.y = 1;
            }
            return this.$store.dispatch('updateScriptMarker', marker).catch(() => {
                // Conflict toast is handled in marker-actions.
            });
        },
        deleteScriptMarker(markerId) {
            return this.$store.dispatch('deleteScriptMarker', markerId).catch(() => {
                // Conflict toast is handled in marker-actions.
            });
        },
        clearPendingInlineEdit() {
            this.pendingInlineEditMarkerId = null;
        },
        getPointerCoords(e) {
            if (e.clientX !== undefined && e.clientY !== undefined) {
                return { x: e.clientX, y: e.clientY };
            }
            if (e.touches && e.touches[0]) {
                return { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
            return { x: 0, y: 0 };
        },
        async onMarkerContainerPlace(e) {
            if (!this.canPlaceScriptMarker || e.target.closest('.ql-script-marker')) {
                return;
            }
            const bounds = e.currentTarget.getBoundingClientRect();
            const { x: clientX, y: clientY } = this.getPointerCoords(e);
            let x = clientX - bounds.left;
            let y = clientY - bounds.top;
            if (this.pageWidth > 0 && this.pageHeight > 0) {
                x /= this.pageWidth;
                if (x > 1) x = 1;
                y /= this.pageHeight;
                if (y > 1) y = 1;
            }
            const type = this.activeScriptMarkerType;
            const created = await this.addScriptMarker({
                pageIndex: this.pageNumber,
                x,
                y,
                type,
                label: defaultMarkerLabel(type)
            });
            if (created && created.id) {
                this.pendingInlineEditMarkerId = created.id;
            }
        },
        onMarkerContainerTouchStart(e) {
            if (!this.canPlaceScriptMarker || e.target.closest('.ql-script-marker')) {
                return;
            }
            this.clearMarkerLongPressTimer();
            this.markerLongPressTimer = setTimeout(() => {
                this.onMarkerContainerPlace(e);
            }, 400);
        },
        onMarkerContainerTouchEnd() {
            this.clearMarkerLongPressTimer();
        },
        onMarkerContainerTouchMove() {
            this.clearMarkerLongPressTimer();
        },
        clearMarkerLongPressTimer() {
            if (this.markerLongPressTimer) {
                clearTimeout(this.markerLongPressTimer);
                this.markerLongPressTimer = null;
            }
        },
        openDialog(ref) {
            this.$refs[ref].open();
        },
        closeDialog(ref) {
            if (this.$refs[ref]) {
                this.$refs[ref].close();
            }
        },
        getPDFViewport() {
            return this.page.pdfPage.getViewport(this.selectedScale, this.page.pdfPage.pageInfo.rotate);
        },
        readyPage() {
            this.ready = false;
            let bounds = {};
            bounds = this.$el.querySelector('.ql-page-cover').getBoundingClientRect();
            const vp = this.getPDFViewport();
            const ph = Math.max(bounds.height, vp.height);
            const pw = Math.max(bounds.width, vp.width);
            this.originWidth = vp.width;
            this.originHeight = vp.height;
            this.pageWidth = pw;
            this.pageHeight = ph;
            this.ready = true;
        },
        setScale(selectedScale) {
            this.scale = selectedScale;
        },
        copyElement(data) {
            this.$emit('copyElement', data);
        },
        pdfLoaded(data) {
            this.setOffset();
            this.setScale(data.viewport.scale);
            this.$emit('pageLoaded', {
                ...data,
                offsetX: this.offsetX,
                offsetY: this.offsetY,
                width: this.pdfWidth,
                height: this.pdfHeight,
                pageWidth: this.pageWidth,
                pageHeight: this.pageHeight,
                markerWidth: this.cueMarkerWidth,
                cueMarkerHeight: this.cueMarkerHeight
            });
            this.pageReady = true;

            if (this.projectBannerDisplay && (this.projectBannerUserID !== this.user.uid)) {
                this.openDialog('banner-dialog');
            }
        },
        pageColumnWidth() {
            if (this.pageReady) {
                document.querySelector('.ql-project-header-column-left');
                return {
                    maxWidth: `${this.pageWidth}px`
                };
            }
            return {};
        },
        cueCreated(tempCue) {
            if (this.locked || this.addingCue) {
                return;
            }
            // Never save a cue without a real position (lib/cue-geometry.js).
            if (!cueGeometryOk(tempCue)) {
                return;
            }

            const filter = this.editableLayers.filter(l => l.id === this.currentCueLayer.id);
            if (filter.length <= 0) {
                return;
            }

            this.$nextTick(() => {
                this.addingCue = false;
                this.cueToAdd = {
                    ...tempCue,
                    title: '',
                    description: tempCue.text || '',
                    pageNumber: this.pageNumber
                };
                this.addCueX = tempCue.x + (this.offsetX - 25);
                this.addCueY = tempCue.y - 5;
                this.addingCue = true;
                this.addCue();
            });
        },
        // Cmd-V of a copied cue (views/project.vue, lib/cue-paste.js). The page under the click takes it, with the
        // same checks and the same placement as its own Cue tool, then adds it through addCue below.
        pasteCue({ cue, clientX, clientY, claim, done }) {
            const canvas = this.$el && this.$el.querySelector && this.$el.querySelector('.canvasWrapper canvas');
            if (!canvas) return;
            const bounds = canvas.getBoundingClientRect();
            if (clientX < bounds.left || clientX > bounds.right || clientY < bounds.top || clientY > bounds.bottom) return;
            if (claim) claim();
            const refuse = () => { if (done) done(null); };
            if (!this.project.isSample && !this.user.active_pay && !this.issrc && !this.project.unlocked) {
                this.openDialog('no-collaborator-dialog');
                refuse();
                return;
            }
            if (!this.currentCueLayer || (this.showCuesLayerIDs !== null && !this.showCuesLayerIDs.includes(this.currentCueLayer.id))) {
                this.openDialog('no-layers-dialog');
                refuse();
                return;
            }
            const editable = this.editableLayers.some(l => l.id === this.currentCueLayer.id);
            if (this.locked || this.addingCue || !editable) {
                refuse();
                return;
            }
            const x = clientX - bounds.x;
            let y = clientY - bounds.y;
            if (cue.type === 'position') {
                // As pdf-page createPositionCue places a new marker.
                y = y <= 30 ? 15 : y - (14 * this.selectedScale);
            }
            this.addingCue = true;
            this.cueToAdd = {
                ...cueForPaste(cue),
                x,
                y,
                pageNumber: this.pageNumber
            };
            this.onCueAdded = cueData => done && done(cueData);
            this.addCue();
        },
        // Structure mark paste (Josh, 22 Oct evening): keeps the copied mark's type, name and number; lands where you
        // last pressed, placed as the Structure tool places a mark; its act is worked out from where it lands.
        pasteStructureMark({ mark, clientX, clientY, claim, done }) {
            const container = this.$el && this.$el.querySelector && this.$el.querySelector('.marker-container');
            if (!container) return;
            const bounds = container.getBoundingClientRect();
            if (clientX < bounds.left || clientX > bounds.right || clientY < bounds.top || clientY > bounds.bottom) return;
            if (claim) claim();
            if (!this.canEditStructure || this.locked) {
                if (done) done(null);
                return;
            }
            let x = clientX - bounds.left;
            let y = clientY - bounds.top;
            if (this.pageWidth > 0 && this.pageHeight > 0) {
                x = Math.min(x / this.pageWidth, 1);
                y = Math.min(y / this.pageHeight, 1);
            }
            this.addScriptMarker({ ...markForPaste(mark), pageIndex: this.pageNumber, x, y })
                .then((created) => { if (done) done(created); })
                .catch(() => { if (done) done(null); });
        },
        addCue() {
            if (this.locked) return;

            if (this.cueToAdd.x > 1 || this.cueToAdd.y > 1) {
                this.cueToAdd.x /= this.pageWidth;
                if (this.cueToAdd.x > 1) this.cueToAdd.x = 1;
                this.cueToAdd.y /= this.pageHeight;
                if (this.cueToAdd.y > 1) this.cueToAdd.y = 1;
            }
            const onAdded = this.onCueAdded;
            this.onCueAdded = null;
            this.$store.dispatch('addCue', this.cueToAdd).then((cueData) => {
                // Auto Numbering was on but had no starting number, so the cue
                // came out unnumbered. Say so ONCE per layer -- see
                // autoNumberWarning.js for why not once per cue.
                if (cueData.autoNumberSkipped === 'no-seed' && this.$toast
                    && shouldWarnNoSeed(this.project && this.project.id, cueData.layerID)) {
                    this.$toast.info('Auto Numbering is on for this layer but has no starting number, so the cue was added without one. Set one in the layer settings.');
                }
                if (onAdded) {
                    // A paste: no focus, so the next Cmd-V pastes again instead of typing into the number.
                    this.addingCue = false;
                    this.cueToAdd = null;
                    this.sortCues();
                    onAdded(cueData);
                    return;
                }
                const numberElement = document.querySelector('#cue-header-number-' + cueData.id);
                const labelElement = document.querySelector('#cue-header-label-' + cueData.id);
                const timeElement = document.querySelector('#cue-header-time-' + cueData.id);

                if (cueData.autoNumbered) {
                    // The number field was auto-filled, so move focus to the
                    // field after it (label, then time). If no subsequent field
                    // is visible, leave the cue unfocused.
                    if (labelElement) {
                        labelElement.click();
                    } else if (timeElement) {
                        timeElement.click();
                    }
                } else if (numberElement) {
                    numberElement.click();
                } else if (labelElement) {
                    labelElement.click();
                }
                this.addingCue = false;
                this.cueToAdd = null;
                this.sortCues();
            }).catch((e) => {
                // A failed Cue tool add behaves as before; a failed paste frees the page and the paste guard.
                if (!onAdded) throw e;
                this.addingCue = false;
                this.cueToAdd = null;
                onAdded(null);
            });
        },
        destroyCue(cueToDestroy) {
            this.$store.dispatch('deletedCue', cueToDestroy);
        },
        updateCue(cueToUpdate) {
            if (cueToUpdate.x > 1 || cueToUpdate.y > 1) {
                cueToUpdate.x /= this.pageWidth;
                if (cueToUpdate.x > 1) cueToUpdate.x = 1;
                cueToUpdate.y /= this.pageHeight;
                if (cueToUpdate.y > 1) cueToUpdate.y = 1;
            }
            this.$store.dispatch('updateCue', cueToUpdate);
        },
        updateNote(noteToUpdate) {
            if (noteToUpdate.text !== '') {
                if (noteToUpdate.x > 1 || noteToUpdate.y > 1) {
                    noteToUpdate.x /= this.pageWidth;
                    if (noteToUpdate.x > 1) noteToUpdate.x = 1;
                    noteToUpdate.y /= this.pageHeight;
                    if (noteToUpdate.y > 1) noteToUpdate.y = 1;
                }
                this.$store.dispatch('updateUserNote', {note: noteToUpdate});
            }
        },
        addNote({data, type}) {
            if (!this.project.isSample) {
                if (!this.user.active_pay) {
                    if (!this.issrc && !this.project.unlocked) {
                        this.openDialog('no-collaborator-dialog');
                        return;
                    }
                }
            }
            if (!this.currentNoteLayer || !this.showNotesLayerIDs.includes(this.currentNoteLayer.id)) {
                this.openDialog('no-layers-dialog');
                return;
            }
            if (this.locked) return;

            this.$nextTick(() => {
                const note = {
                    ...data,
                    type,
                    pageNumber: this.pageNumber
                };
                this.$store.dispatch('addUserNote', {
                    note,
                    layer: this.currentNoteLayer
                }).then((noteData) => {
                    const noted = {
                        ...data,
                        historyType: 'add',
                        layer: this.currentNoteLayer.id,
                        id: noteData.id,
                        x: note.x,
                        y: note.y,
                        type,
                        pageNumber: this.pageNumber
                    };
                    this.updatePageHistory(noted);
                    if (noteData.type !== 'image' && noteData.type !== 'text' && noteData.type !== 'stamp') return;
                    setTimeout(() => {
                        const noteRef = document.querySelector('#' + noteData.type + '-note-' + noteData.id);
                        noteRef.parentElement.click();
                        noteRef.focus();
                    }, 100);
                });
            });
        },
        cancelAddCue() {
            this.addingCue = false;
            this.cueToAdd = null;
        },
        sortCues() {
            if (this.cues) {
                this.cues.sort(comparator);
            }
        },

        cueColor(cue) {
            const layer = this.layerByCue(cue);
            if (layer) return layer.color;

            return 'black';
        },
        setOffset() {
            const page = this.$el.querySelector('.ql-pdf-page');
            if (page) {
                const bounds = page.getBoundingClientRect();
                this.pdfHeight = bounds.height;
                this.pdfWidth = bounds.width;
                this.offsetY = bounds.y;
                this.$store.dispatch('updatePageOffset', bounds.x);
            }
        },
        resize() {
            this.setOffset();
            if (this.pageReady) {
                this.$emit('pageLoaded', {
                    offsetX: this.offsetX,
                    offsetY: this.offsetY,
                    width: this.pdfWidth,
                    height: this.pdfHeight,
                    pageWidth: this.pageWidth,
                    pageHeight: this.pageHeight,
                    markerWidth: this.cueMarkerWidth,
                    cueMarkerHeight: this.cueMarkerHeight
                });
            }
        },
        isCueEditable(cue) {
            const layer = this.layerByCue(cue);
            if (layer) {
                const filter = this.editableLayers.filter(l => l.id === layer.id);
                return filter.length > 0;
            }
            return false;
        },
        isNoteEditable(note) {
            const filter = this.editableLayers.filter(l => l.id === note.layer);
            return filter.length > 0;
        },
        toggleBookmarkNav() {
            this.$emit('toggleBookmarkNav');
        },
        setSelectedElement(data) {
            this.$emit('setSelectedElement', data);
        },
        updatePageHistory(data) {
            this.$emit('updatePageHistory', data);
        },
        togglePageBookmark() {
            if (this.bookmarked) {
                const removeBookmarkName = this.versionID ? 'removeVersionBookmark' : 'removeBookmark';
                this.$store.dispatch(removeBookmarkName, {
                    versionID: this.versionID,
                    pageNumber: this.pageNumber
                });
            } else {
                if (this.versionID) {
                    this.$store.dispatch('addVersionBookmark', {
                        versionID: this.versionID,
                        pageNumber: this.pageNumber,
                        pageTitle: this.currentVersion.pdfPageNumbers && this.currentVersion.pdfPageNumbers[this.pageNumber - 1] && this.currentVersion.pdfPageNumbers[this.pageNumber - 1].name
                    });
                }
                if (this.versionID === '') {
                    this.$store.dispatch('addBookmark', {
                        pageNumber: this.pageNumber,
                        pageTitle: this.project.pdfPageNumbers && this.project.pdfPageNumbers[this.pageNumber - 1] && this.project.pdfPageNumbers[this.pageNumber - 1].name
                    });
                }
                // Opens the panel if closed, switches to My Bookmarks either
                // way, and asks the new row to open its title field focused.
                this.$emit('openBookmarkNav', 'bookmarks', this.pageNumber);
            }
        },
        async closeAlert() {
            await this.$store.dispatch('projectBannerDisplay', { projectBannerDisplay: false });
        }
    },
    components: {
        'cue-position-marker': CuePositionMarker,
        'cue-selection-marker': CueSelectionMarker,
        'resize-sensor': resizeSensor,
        'project-pdf-page': ProjectPdfPage,
        'project-note-template': ProjectNoteTemplate,
        'script-marker-overlay': ScriptMarkerOverlay
    },
    mounted: function() {
        this.readyPage();
        window.addEventListener('keydown', this.onAltKey);
        window.addEventListener('keyup', this.onAltKey);
        window.addEventListener('blur', this.clearAltKey);
        NavBus.$on('paste-cue', this.pasteCue);
        NavBus.$on('paste-structure-mark', this.pasteStructureMark);
    },
    beforeDestroy() {
        NavBus.$off('paste-cue', this.pasteCue);
        NavBus.$off('paste-structure-mark', this.pasteStructureMark);
        this.clearMarkerLongPressTimer();
        window.removeEventListener('keydown', this.onAltKey);
        window.removeEventListener('keyup', this.onAltKey);
        window.removeEventListener('blur', this.clearAltKey);
    }
};

export default ProjectPage;
</script>

<style lang="scss">

.marker-container, .ql-note-container {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1001;
    pointer-events: none;
}
.marker-container {
    z-index: 1002 !important;
}
.marker-container.marker-container--place-mode {
    cursor: crosshair;
    pointer-events: auto !important;
}
.marker-container > .ql-script-marker,
.marker-container > .vdr.ql-script-marker {
    pointer-events: fill !important;
}
/* Over structure marks (z-index 100; a mark open for editing is 110):
   - cue markers at 105 draw above them AND take the press where they overlap (Josh, 10 Oct 2026: editing the cue box
     is far more likely than moving the mark). Holding Option/Alt (marker-container--reach-marks) lets the press fall
     through to the mark underneath. No tablet equivalent yet; on a tablet the cue wins.
   - draw-only copies of free drawings at 102: above the marks, below cues, never take a press. The real drawing (in
     .ql-note-container, under this container) keeps the clicks and is hidden while its copy shows. */
.marker-container > .ql-position-cue-marker,
.marker-container > .ql-selection-cue-marker {
    z-index: 105 !important;
}
.marker-container.marker-container--reach-marks > .ql-position-cue-marker,
.marker-container.marker-container--reach-marks > .ql-selection-cue-marker,
.marker-container.marker-container--reach-marks > .ql-position-cue-marker *,
.marker-container.marker-container--reach-marks > .ql-selection-cue-marker * {
    pointer-events: none !important;
}
.marker-container > .ql-drawing-paint-only {
    position: absolute;
    top: 0;
    left: 0;
    box-sizing: border-box;
    z-index: 102 !important;
    pointer-events: none !important;
}
.ql-note-container > .ql-drawing-hit-only {
    opacity: 0 !important;
}
.ql-drawing-overlay {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1001;
    pointer-events: none;
}

.ql-page {
    width: 100%;
    overflow: visible;
    position: relative !important;
}
.ql-page.disable-note-container .ql-note-container > div,
.ql-page.disable-note-container > div.marker-container > div.ql-selection-cue-marker,
.ql-page.disable-note-container > div.marker-container > div.ql-position-cue-marker,
.ql-cue-list.disable-note-container > div.ql-cue{
    pointer-events: none!important;
}
/* Structure marks too, while a create-drag (cue box, highlight, Draw stroke) is
   in progress. disable-note-container is the page-level "drag in progress" flag
   (pdf-page.vue startDrag sets it, endDrag clears it). Without this, moving
   onto a mark fires mouseout on the page, whose stopDrag ends the drag early
   and saves a short box or stroke. The mark's label and action bar set their
   own pointer-events, so its descendants are covered as well. */
.ql-page.disable-note-container > div.marker-container > div.ql-script-marker,
.ql-page.disable-note-container > div.marker-container > div.ql-script-marker * {
    pointer-events: none!important;
}

.ql-add-cue-button {
    position: absolute;
    z-index: 1001;
}

.ql-page-cover {
    background-color: #333;
}

.md-theme-default {
    .ql-page-cover {
        background-color: #ddd;
    }
}

.ql-pdf-page {
    margin: 0 auto;
    position: relative;
}

.night-mode {
    -webkit-filter: invert(88%) !important;
    filter: invert(88%) !important;
}

.disable-text {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}

.alert-banner-dialog {
    top: 20px !important;
    position: fixed !important;
    margin: 0 auto;
    width: 90%;
    max-width: 740px;
    z-index: 9999;
    border-radius: 12px;
    overflow: hidden;

    .md-dialog {
        border-radius: 10px;
    }
    .md-backdrop.md-active {
        background-color: rgba(0, 0, 0, 0.9) !important;
    }
}
.alert-banner-dialog:focus-visible {
  outline: none !important;
  box-shadow: none !important;
}

.alert-content {
  padding: 0;
}

.alert-box {
  display: flex;
  align-items: flex-start;
  padding: 1.25rem;
  margin-top: -25px;
}

.alert-icon-box {
  background-color: #73D5E3;
  border-radius: 50%;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.alert-icon {
  color: #fff;
}

.alert-text-box {
  flex: 1;
  margin-top: -15px;
}

.alert-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 0.4rem;
  color: #73D5E3;
}

.alert-message {
  font-size: 15px;
  color: #5d4037;
  line-height: 1.4;
}

.md-theme-show-mode {
    .alert-icon-box {
        background-color: #333333;
    }
    .alert-content {
        background-color: #424242 !important;
    }
    .alert-title {
        color: #fff;
    }
    .alert-message {
        color: #fff;
    }
}
</style>


// WEBPACK FOOTER //
// page.vue?46a80737